// Public account-deletion page rendered at /delete-account. Required by
// Google Play / App Store policies for any app behind a sign-in. Visitors
// without an account can read what deletion does and email the team; signed-in
// visitors can delete immediately via the existing deleteAccount() flow.
import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'

const GOLD = 'var(--gold)'

export default function DeleteAccountPage({ onNavigate }) {
  const { user, profile, deleteAccount } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [deleting,   setDeleting]   = useState(false)
  const [done,       setDone]       = useState(false)
  const [error,      setError]      = useState('')

  useEffect(() => {
    const prev = document.title
    document.title = 'Delete your account · The Bible Community'
    return () => { document.title = prev }
  }, [])

  function goBack(e) {
    e?.preventDefault?.()
    if (onNavigate) onNavigate('/')
    else if (typeof window !== 'undefined') window.location.href = '/'
  }

  function goLogin(e) {
    e?.preventDefault?.()
    if (onNavigate) onNavigate('/login')
    else if (typeof window !== 'undefined') window.location.href = '/login'
  }

  async function handleDelete() {
    setDeleting(true); setError('')
    const { ok, error: delErr } = await deleteAccount()
    setDeleting(false)
    if (ok) {
      setDone(true)
      setConfirming(false)
    } else {
      setError(delErr?.message || 'Could not delete your account. Please try again or contact us.')
    }
  }

  const displayEmail = profile?.email || user?.email || ''

  return (
    <div style={S.shell}>
      <div style={S.bar}>
        <a href="/" onClick={goBack} style={S.back}>← Back</a>
        <div style={S.brand}>The Bible Community</div>
      </div>

      <article style={S.article}>
        <h1 style={S.h1}>Delete your account</h1>
        <p style={S.lead}>
          Deleting your account removes your data from The Bible Community permanently. This
          can't be undone — but if you're sure, you can do it in a couple of clicks below.
        </p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>What gets deleted</h2>
        <ul style={S.ul}>
          <li>Your profile (name, email, photo) and your sign-in details</li>
          <li>Your saved verses, notes, prayer requests, comments and community posts</li>
          <li>Your church memberships and follows</li>
        </ul>
        <p style={S.p}>
          Anonymised, aggregated data (e.g. counts used for site analytics) and any content
          required to be kept for legal or audit reasons may be retained — see the{' '}
          <a href="/privacy" style={S.a} onClick={e => { e.preventDefault(); onNavigate ? onNavigate('/privacy') : (window.location.href = '/privacy') }}>privacy policy</a>{' '}
          for details.
        </p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>Delete now</h2>

        {done ? (
          <div style={S.successBox}>
            <div style={S.successTitle}>Your account has been deleted.</div>
            <div style={S.successBody}>You've been signed out and your data has been removed.</div>
            <a href="/" onClick={goBack} style={{...S.btn, marginTop:18, display:'inline-block', textDecoration:'none'}}>Back to home</a>
          </div>
        ) : user ? (
          <>
            <p style={S.p}>
              You're signed in as <strong style={{color:'var(--ink)'}}>{displayEmail}</strong>. Click below to
              delete this account now.
            </p>
            {!confirming ? (
              <button type="button" style={S.dangerBtn} onClick={() => setConfirming(true)}>
                Delete my account
              </button>
            ) : (
              <div style={S.confirmBox}>
                <div style={S.confirmTitle}>Are you sure?</div>
                <div style={S.confirmBody}>
                  This permanently deletes <strong style={{color:'var(--ink)'}}>{displayEmail}</strong>{' '}
                  and all your content. It cannot be undone.
                </div>
                <div style={{display:'flex', gap:10, marginTop:18, flexWrap:'wrap'}}>
                  <button type="button" disabled={deleting} style={S.dangerBtn} onClick={handleDelete}>
                    {deleting ? 'Deleting…' : 'Yes, delete forever'}
                  </button>
                  <button type="button" disabled={deleting} style={S.cancelBtn} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {error && <div style={S.err}>{error}</div>}
          </>
        ) : (
          <>
            <p style={S.p}>
              To delete your account immediately, sign in below — once signed in this page will
              show a "Delete my account" button.
            </p>
            <a href="/login" onClick={goLogin} style={{...S.btn, display:'inline-block', textDecoration:'none'}}>
              Sign in to delete →
            </a>

            <div style={{...S.infoBox, marginTop:28}}>
              <div style={S.infoBoxTitle}>Can't sign in?</div>
              <div style={S.infoBoxBody}>
                Email{' '}
                <a href="mailto:hello@thebiblecommunity.co.uk?subject=Delete%20my%20account" style={S.a}>
                  hello@thebiblecommunity.co.uk
                </a>{' '}
                from the address on your account and we'll delete it within 30 days.
              </div>
            </div>
          </>
        )}

        <hr style={S.hr}/>

        <p style={S.foot}>
          Questions? Email{' '}
          <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a>.
        </p>
      </article>
    </div>
  )
}

const S = {
  shell:       { minHeight: '100vh', background: 'var(--cream)', color: 'var(--ink)', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
  bar:         { position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'rgba(246,242,236,0.92)', backdropFilter: 'blur(8px)' },
  back:        { color: 'var(--gold-ink)', fontSize: 13, textDecoration: 'none', fontWeight: 700, letterSpacing: '0.04em' },
  brand:       { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold-ink)' },
  article:     { maxWidth: 720, margin: '0 auto', padding: '40px 24px 80px' },
  h1:          { fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 'clamp(32px, 6vw, 44px)', lineHeight: 1.1, marginBottom: 14 },
  h2:          { fontSize: 22, fontWeight: 700, marginTop: 32, marginBottom: 12, color: 'var(--ink)' },
  lead:        { fontSize: 16, lineHeight: 1.7, color: 'var(--ink)' },
  p:           { fontSize: 15, lineHeight: 1.7, color: 'var(--ink)', marginBottom: 12 },
  ul:          { fontSize: 15, lineHeight: 1.8, color: 'var(--ink)', marginBottom: 12, paddingLeft: 24 },
  a:           { color: 'var(--gold-ink)', textDecoration: 'underline' },
  hr:          { border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' },
  foot:        { fontSize: 13, color: 'var(--stone)', marginTop: 28 },

  btn:         { background: GOLD, color: 'var(--ink)', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", letterSpacing: '0.02em' },
  dangerBtn:   { background: '#F97066', color: 'var(--ink)', border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", letterSpacing: '0.02em' },
  cancelBtn:   { background: 'transparent', color: 'var(--stone)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },

  confirmBox:  { background: 'rgba(249,112,102,0.06)', border: '1px solid rgba(249,112,102,0.3)', borderRadius: 12, padding: '18px 20px', marginTop: 12 },
  confirmTitle:{ fontSize: 15, fontWeight: 800, color: '#C8372A', marginBottom: 6 },
  confirmBody: { fontSize: 14, lineHeight: 1.6, color: 'var(--ink)' },

  successBox:  { background: 'rgba(232,200,122,0.08)', border: '1px solid rgba(232,200,122,0.3)', borderRadius: 12, padding: '20px 22px' },
  successTitle:{ fontSize: 17, fontWeight: 800, color: 'var(--gold-ink)', marginBottom: 6 },
  successBody: { fontSize: 14, lineHeight: 1.6, color: 'var(--ink)' },

  infoBox:     { background: 'rgba(31,27,22,0.04)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' },
  infoBoxTitle:{ fontSize: 13, fontWeight: 800, color: 'var(--ink)', marginBottom: 6, letterSpacing: '0.02em' },
  infoBoxBody: { fontSize: 14, lineHeight: 1.6, color: 'var(--stone)' },

  err:         { marginTop: 16, padding: '12px 14px', background: 'rgba(249,112,102,0.1)', border: '1px solid rgba(249,112,102,0.3)', borderRadius: 10, color: '#C8372A', fontSize: 13, lineHeight: 1.5 },
}
